import ProjectManagerService, {
  DrumsetFile,
  SongFile,
  SongFolder,
} from 'editor/services/project-manager';
import Command from './command';
import type Drumset from './drumset';

export class RenameFolderCommand extends Command<SongFolder> {
  folder: SongFolder;
  fromName: string;
  toName: string;

  constructor(folder: SongFolder, name: string) {
    super('Rename Folder');
    this.folder = folder;
    this.fromName = folder.name;
    this.toName = name;
  }

  markDirty(): void {
    this.folder.markDirty();
  }

  execute() {
    this.folder.name = this.toName;
    return this.folder;
  }

  reverse() {
    this.folder.name = this.fromName;
    return this.folder;
  }
}

export class SortFoldersSongsAlphabeticallyCommand extends Command<void> {
  project: ProjectManagerService;
  folders: SongFolder[];

  constructor(project: ProjectManagerService) {
    super('Sort Folders & Songs Alphabetically');
    this.project = project;
    this.folders = project.songFolders.map((f) => {
      const folder = new SongFolder(f.handle, f.name, f.path);
      folder.songs = [...f.songs];
      return folder;
    });
  }

  markDirty(): void {
    this.project.songFoldersDirty = true;
  }

  execute() {
    const ignoreArticles = (str) => str.replace(/^(the|a|an)\s+/i, '');

    if (this.project.sortMethod === 'AtoZ') {
      this.project.songFolders = this.project.songFolders.toSorted((d1, d2) =>
        ignoreArticles(d1.name).localeCompare(ignoreArticles(d2.name))
      );
      this.project.songFolders.forEach((folder) => {
        folder.songs = folder.songs.toSorted((s1, s2) =>
          ignoreArticles(s1.name).localeCompare(ignoreArticles(s2.name))
        );
      });
    } else if (this.project.sortMethod === 'ZtoA') {
      this.project.songFolders = this.project.songFolders.toSorted((d1, d2) =>
        ignoreArticles(d2.name).localeCompare(ignoreArticles(d1.name))
      );
      this.project.songFolders.forEach((folder) => {
        folder.songs = folder.songs.toSorted((s1, s2) =>
          ignoreArticles(s2.name).localeCompare(ignoreArticles(s1.name))
        );
      });
    }
  }

  reverse() {
    this.project.songFolders = this.folders;
  }
}

export class SortFoldersCommand extends Command<void> {
  project: ProjectManagerService;
  oldIndex: number;
  newIndex: number;

  constructor(
    project: ProjectManagerService,
    oldIndex: number,
    newIndex: number,
  ) {
    super('Sort Folders');
    this.project = project;
    this.oldIndex = oldIndex;
    this.newIndex = newIndex;
  }

  markDirty(): void {
    this.project.songFoldersDirty = true;
  }

  execute() {
    const folder = this.project.songFolders[this.oldIndex];
    const arr = [...this.project.songFolders];
    if (folder) {
      // remove folder from oldIndex and insert it at newIndex
      arr.splice(this.oldIndex, 1);
      arr.splice(this.newIndex, 0, folder);
    }
    this.project.songFolders = arr.map((f) => {
      const folder = new SongFolder(f.handle, f.name, f.path);
      folder.songs = [...f.songs];
      return folder;
    });
  }

  reverse() {
    const folder = this.project.songFolders[this.newIndex];
    const arr = [...this.project.songFolders];
    if (folder) {
      // remove folder from newIndex and insert it at oldIndex
      arr.splice(this.newIndex, 1);
      arr.splice(this.oldIndex, 0, folder);
    }
    this.project.songFolders = arr.map((f) => {
      const folder = new SongFolder(f.handle, f.name, f.path);
      folder.songs = [...f.songs];
      return folder;
    });
  }
}

export class SortDrumFoldersCommand extends Command<void> {
  project: ProjectManagerService;
  oldIndex: number;
  newIndex: number;

  constructor(
    project: ProjectManagerService,
    oldIndex: number,
    newIndex: number,
  ) {
    super('Sort Folders');
    this.project = project;
    this.oldIndex = oldIndex;
    this.newIndex = newIndex;
  }

  markDirty(): void {
    this.project.drumFoldersDirty = true;
  }

  execute() {
    const folder = this.project.drumFolders[this.oldIndex];
    if (folder) {
      this.project.drumFolders.splice(this.oldIndex, 1);
      this.project.drumFolders.splice(this.newIndex, 0, folder);
    }
  }

  reverse() {
    const folder = this.project.drumFolders[this.newIndex];
    if (folder) {
      this.project.drumFolders.splice(this.newIndex, 1);
      this.project.drumFolders.splice(this.oldIndex, 0, folder);
    }
  }
}

export class SortSongsCommand extends Command<SongFolder> {
  folder: SongFolder;
  oldIndex: number;
  newIndex: number;

  constructor(folder: SongFolder, oldIndex: number, newIndex: number) {
    super('Sort Songs');
    this.folder = folder;
    this.oldIndex = oldIndex;
    this.newIndex = newIndex;
  }

  markDirty(): void {
    this.folder.markDirty();
  }

  execute() {
    const song = this.folder.songs[this.oldIndex];
    this.folder.addSong(song!, this.newIndex);
    this.folder.removeSong(this.oldIndex);

    return this.folder;
  }

  reverse() {
    const song = this.folder.songs[this.newIndex];
    this.folder.addSong(song!, this.oldIndex);
    this.folder.removeSong(this.newIndex);

    return this.folder;
  }
}

export class MoveSongCommand extends Command<SongFile> {
  fromFolder: SongFolder;
  fromIndex: number;
  toFolder: SongFolder;
  toIndex?: number;

  constructor(
    fromFolder: SongFolder,
    fromIndex: number,
    toFolder: SongFolder,
    toIndex?: number,
  ) {
    super('Move Song');
    this.fromFolder = fromFolder;
    this.fromIndex = fromIndex;
    this.toFolder = toFolder;
    this.toIndex = toIndex;
  }

  markDirty(): void {
    this.fromFolder.markDirty();
    this.toFolder.markDirty();
  }

  async execute() {
    const song = await this.fromFolder.removeSong(this.fromIndex);
    if (!song) {
      throw new Error('Song not found');
    }
    await this.toFolder.addSong(song, this.toIndex);
    if (this.toIndex === undefined) {
      this.toIndex = this.toFolder.songs.indexOf(song);
    }
    return song;
  }

  async reverse() {
    const song = await this.toFolder.removeSong(this.toIndex!);
    if (!song) {
      throw new Error('Song not found');
    }
    await this.fromFolder.addSong(song, this.fromIndex);
    return song;
  }
}

export class MoveDrumCommand extends Command<SongFile> {
  fromFolder: SongFolder;
  fromIndex: number;
  toFolder: SongFolder;
  toIndex?: number;
  project?: ProjectManagerService;

  constructor(
    fromFolder: SongFolder,
    fromIndex: number,
    toFolder: SongFolder,
    toIndex?: number,
    project?: ProjectManagerService,
  ) {
    super('Move Song');
    this.fromFolder = fromFolder;
    this.fromIndex = fromIndex;
    this.toFolder = toFolder;
    this.toIndex = toIndex;
    this.project = project;
  }

  markDirty(): void {
    if (this.fromFolder) {
      this.fromFolder.markDirty();
    }
    if (this.toFolder) {
      this.toFolder.markDirty();
    }
  }

  async execute() {
    const song = this.fromFolder ?
                  await this.fromFolder.removeSong(this.fromIndex) // move from folder to folder
                    : this.project!.drumsOnTheLoose[this.fromIndex]; // move from loose drums list to folder
    if (!song) {
      throw new Error('Song not found');
    }
    if (!this.fromFolder) { // move from loose drums list to folder
      this.project!.drumsOnTheLoose = this.project!.drumsOnTheLoose.toSpliced(
        this.fromIndex,
        1,
      );
    }
    if (this.toFolder) { // move from folder or loose drums list to folder
      await this.toFolder.moveDrumIntoFolder(song, this.toIndex);
    } else { // move from folder to loose drums list
      this.project!.drumsOnTheLoose = this.project!.drumsOnTheLoose.toSpliced(
        this.toIndex,
        0,
        song,
      );
    }
    if (!this.toIndex) {
      this.toIndex = this.toFolder.songs.indexOf(song);
    }
    return song;
  }

  async reverse() {
    const song = this.toFolder ?
                  await this.toFolder.removeSong(this.toIndex!) // reversing a move from folder or loose drum list to folder
                      : this.project.drumsOnTheLoose[this.toIndex!]; // reversing a move from folder to loose drums list
    if (!song) {
      throw new Error('Song not found');
    }
    if (!this.toFolder) {
      this.project.drumsOnTheLoose = this.project.drumsOnTheLoose.toSpliced(
        this.toIndex!,
        1,
      );
    }
    if (this.fromFolder) {
      await this.fromFolder.moveDrumIntoFolder(song, this.fromIndex);
    } else {
      this.project.drumsOnTheLoose = this.project.drumsOnTheLoose.toSpliced(
        this.fromIndex,
        0,
        song,
      );
    }
    return song;
  }
}

export class SortDrumsetsFolderCommand extends Command<SongFolder> {
  folder: SongFolder;
  oldIndex: number;
  newIndex: number;

  constructor(folder: SongFolder, oldIndex: number, newIndex: number) {
    super('Sort Songs');
    this.folder = folder;
    this.oldIndex = oldIndex;
    this.newIndex = newIndex;
  }

  markDirty(): void {
    this.folder.markDirty();
  }

  async execute() {
    const song = this.folder.songs[this.oldIndex];
    this.folder.removeSong(this.oldIndex);
    this.folder.moveDrumIntoFolder(song!, this.newIndex);
    return this.folder;
  }

  async reverse() {
    const song = this.folder.songs[this.newIndex];
    await this.folder.removeSong(this.newIndex);
    await this.folder.moveDrumIntoFolder(song!, this.oldIndex);
    return this.folder;
  }
}


export class AddFolderCommand extends Command<SongFolder> {
  project: ProjectManagerService;
  folder?: SongFolder;
  isDrumFolder: boolean;

  constructor(project: ProjectManagerService, name: string = 'New Folder', isDrumFolder: boolean = false) {
    super('Add Folder');
    this.project = project;
    this.name = name;
    this.isDrumFolder = isDrumFolder;
  }

  markDirty(): void {
    if (this.isDrumFolder) {
      this.project.drumFoldersDirty = true;
    } else {
      this.project.songFoldersDirty = true;
    }
    this.folder?.markDirty();
  }

  async execute() {
    this.folder = await this.project.createSongFolder(this.name, this.isDrumFolder);
    return this.folder;
  }

  async reverse() {
    if (!this.folder) {
      throw new Error('Folder not found');
    }
    this.project.removeFolder(this.folder);
    return this.folder;
  }
}
export class DeleteSongFolderCommand extends Command<SongFolder> {
  projectManager: ProjectManagerService;
  folder: SongFolder;
  index: number;
  isDrumFolder: boolean;
  songs: (SongFile | DrumsetFile)[];
  // deletedDrumsets?: DrumsetFile[] = [];
  // TP for bug:
  // 1. delete drum folder
  // 2. save
  // 3. undo
  // 4. save (check hex editor of file)
  // check root level config.csv - are drums from drumfolder in there? (they shouldn't be, they should be in folder config)
  // this caused me to have a data out of bounds thing, when it was trying to parse a drum set in there
  // so I think two issues are:
  // 1. delete save undo save causes drumset file with no data (check hex editor)
  // 2. drumsets in root config.csv that should be in folder config.csv
  // but needs more looking into

  constructor(projectManager: ProjectManagerService, folder: SongFolder, isDrumFolder: boolean = false) {
    super('Delete Song Folder');
    this.projectManager = projectManager;
    this.index = projectManager.songFolders.findIndex((f) => f === folder);
    this.folder = folder;
    this.isDrumFolder = isDrumFolder;
    this.songs = [...folder.songs];
  }

  markDirty(): void {
    if (this.isDrumFolder) {
      this.projectManager.drumFoldersDirty = true;
    } else {
      this.projectManager.songFoldersDirty = true;
    }
  }

  async execute() {
    if (this.isDrumFolder) {
      // this.deletedDrumsets = this.folder.songs
      // this.projectManager.drumsets = this.projectManager.drumsets.filter((d) => !this.deletedDrumsets!.includes(d));
      this.projectManager.drumFolders = this.projectManager.drumFolders.filter(
        (f) => f !== this.folder,
      );

      this.projectManager.drumsets = this.projectManager.drumsets.filter((drumset) => {
        return !this.songs.find((s) => s === drumset);
      });

      if (this.projectManager.currentDrumset && this.folder.songs.find((s) => s === this.projectManager.currentDrumset)) {
        if (this.projectManager.drumsOnTheLoose.length == 0) {
          this.projectManager.currentDrumset = this.projectManager.drumFolders[0]?.songs[0];
        } else {
          this.projectManager.currentDrumset = this.projectManager.drumsOnTheLoose[0];
        }
      }
    } else {
      this.projectManager.songFolders = this.projectManager.songFolders.filter(
        (f) => f !== this.folder,
      );
    }
    return this.folder;
  }

  async reverse() {
    if (this.isDrumFolder) {
      this.projectManager.drumFolders = this.projectManager.drumFolders.toSpliced(
        this.index,
        0,
        this.folder,
      );
      this.projectManager.drumsets = this.projectManager.drumsets.toSpliced(
        this.projectManager.drumsets.length,
        0,
        ...(this.songs as DrumsetFile[]),
      );
      // there may be problems with the above if you do delete drum folder, undo, save, redo, save
      // see comment below (from a different PR)
        
      // this.projectManager.drumsets = this.projectManager.drumsets.toSpliced(
      //   this.projectManager.drumsets.length,
      //   0,
      //   ...(this.deletedDrumsets || []),
      // );
      // this.folder.songs = [];
      // for (let d = 0; d < (this.deletedDrumsets || []).length; d++) {
      //   this.projectManager.addDrumset(this.deletedDrumsets[d], d, this.folder);
      // }
      // this.deletedDrumsets = [];
    } else {
      this.projectManager.songFolders = this.projectManager.songFolders.toSpliced(
        this.index,
        0,
        this.folder,
      );
    }
    return this.folder;
  }
}
